import { useEffect } from "react";

// Small helper that sets document.title, meta description, and the
// canonical <link> based on the active UI language. Pass a config map
// keyed by lang code: { en: {title, description, canonical?, alternates?}, nl: {...}, es: {...} }.
// Falls back to the "en" entry when the current lang has no entry.
//
// `alternates`, when provided, is an array of { hreflang, href } that
// is rendered as <link rel="alternate" hreflang="..." href="..." /> tags
// under a data-managed marker so it can be safely re-rendered on lang
// changes without duplicating tags.
export const useSeoTags = (lang, configs) => {
  useEffect(() => {
    const cfg = configs[lang] || configs.en || {};
    if (cfg.title) document.title = cfg.title;

    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", cfg.description);

    if (cfg.canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", cfg.canonical);
    }

    // og:title / og:description mirror the current locale so social
    // previews match the language the visitor picked.
    const setProp = (prop, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setProp("og:title", cfg.title);
    setProp("og:description", cfg.description);
    document.documentElement.setAttribute("lang", lang || "en");

    // hreflang alternate <link> tags. All previously-rendered tags with
    // our data-managed marker are removed first to avoid duplicates when
    // the language changes or the caller updates the alternates list.
    document
      .querySelectorAll('link[rel="alternate"][data-managed="seo-hreflang"]')
      .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    (cfg.alternates || []).forEach((a) => {
      if (!a || !a.href || !a.hreflang) return;
      const el = document.createElement("link");
      el.setAttribute("rel", "alternate");
      el.setAttribute("hreflang", a.hreflang);
      el.setAttribute("href", a.href);
      el.setAttribute("data-managed", "seo-hreflang");
      document.head.appendChild(el);
    });
  }, [lang, configs]);
};

// Injects a JSON-LD script tag and cleans it up on unmount.
export const useJsonLd = (id, data) => {
  useEffect(() => {
    if (!data) return undefined;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { if (el && el.parentNode) el.parentNode.removeChild(el); };
  }, [id, data]);
};
